import { useMemo } from 'react';

import { hasUnacceptableAnswer, isQuestionAnswered } from 'Models/DueDiligence';
import { TprmOrUcrmDueDiligenceAssessment } from 'Models/UcrmTprm';

export interface QuestionsStatistics {
    answered: number;
    total: number;
    unacceptable: number;
    unanswered: number;
}

/** Used in conjunction with `QuestionsDashlet`. */
export const useQuestionsStatistics = (assessments: TprmOrUcrmDueDiligenceAssessment[]): Record<string, QuestionsStatistics> => {
    const questionStatistics: Record<string, QuestionsStatistics> = useMemo(() => {
        const stats: Record<string, QuestionsStatistics> = {};

        assessments.forEach((questionnaire) => {
            let total = 0;
            let answered = 0;
            let unacceptable = 0;

            for (const framework of questionnaire.control_frameworks) {
                for (const group of framework.control_groups) {
                    for (const control of group.controls) {
                        for (const question of control.questions) {
                            total++;

                            if (isQuestionAnswered(question)) {
                                answered++;
                                if (hasUnacceptableAnswer(question)) {
                                    unacceptable++;
                                }
                            }
                        }
                    }
                }
            }

            stats[questionnaire.id] = {
                answered,
                total,
                unacceptable,
                unanswered: total - answered,
            };
        });

        return stats;
    }, [assessments]);

    return questionStatistics;
};

/** Combines multiple `QuestionsStatistics` into an overall summary. */
export const aggregateQuestionsStatistics = (statistics: QuestionsStatistics[]): QuestionsStatistics =>
    statistics.reduce<QuestionsStatistics>(
        (aggregate, current) => ({
            answered: aggregate.answered + current.answered,
            total: aggregate.total + current.total,
            unacceptable: aggregate.unacceptable + current.unacceptable,
            unanswered: aggregate.unanswered + current.unanswered,
        }),
        { answered: 0, total: 0, unacceptable: 0, unanswered: 0 }
    );
